import React from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  MenuItem,
  FormHelperText,
  Box,
} from "@mui/material";
import {
  useCreateBaseUnitMutation,
  useUpdateUnitMutation,
} from "../../../../../services/baseQuery";
import { toast } from "react-hot-toast";

const unitCategories = [
  "COUNT",
  "WEIGHT",
  "VOLUME",
  "LENGTH",
  "AREA",
  "PACKAGING",
  "OTHER",
];

export const BaseUnitForm = ({ onClose, initialData, isEditing }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialData,
  });

  const [createUnit, { isLoading: isCreating }] = useCreateBaseUnitMutation();
  const [updateUnit, { isLoading: isUpdating }] = useUpdateUnitMutation();

  const isLoading = isCreating || isUpdating;

  const onSubmit = async data => {
    try {
      if (isEditing) {
        await updateUnit({ id: initialData.id, data }).unwrap();
        toast.success("Unit updated successfully");
      } else {
        await createUnit(data).unwrap();
        toast.success("Base unit created successfully");
      }
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to save unit");
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "0.5rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E5E7EB",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#5652B0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#5652B0",
    },
  };

  return (
    <div className="grid gap-6">
      <div className="grid grid-cols-2 gap-6">
        <TextField
          label="Name"
          {...register("name", { required: "Name is required" })}
          error={!!errors.name}
          helperText={errors.name?.message}
          variant="outlined"
          size="small"
          fullWidth
          sx={inputStyles}
          className="font-outfit"
        />

        <TextField
          label="Abbreviation"
          {...register("abbreviation", {
            required: "Abbreviation is required",
          })}
          error={!!errors.abbreviation}
          helperText={errors.abbreviation?.message}
          variant="outlined"
          size="small"
          fullWidth
          sx={inputStyles}
          className="font-outfit"
        />

        <TextField
          select
          label="Category"
          {...register("category", { required: "Category is required" })}
          error={!!errors.category}
          helperText={errors.category?.message}
          variant="outlined"
          size="small"
          fullWidth
          sx={inputStyles}
          className="font-outfit col-span-2">
          {unitCategories.map(category => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Description"
          {...register("description")}
          multiline
          rows={3}
          variant="outlined"
          size="small"
          fullWidth
          sx={inputStyles}
          className="font-outfit col-span-2"
        />
      </div>

      <div className="bg-gray-50 rounded-lg p-3">
        <p className="text-xs font-medium text-gray-600 mb-2">
          Base Unit Information:
        </p>
        <ul className="text-xs text-gray-500 space-y-1">
          <li className="flex items-center">
            <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
            Base units are fundamental measurement units
          </li>
          <li className="flex items-center">
            <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
            Cannot be broken down into smaller units
          </li>
          <li className="flex items-center">
            <span className="w-1 h-1 rounded-full bg-gray-400 mr-2" />
            Examples: piece, gram, meter, liter
          </li>
        </ul>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          disabled={isLoading}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {isEditing ? "Updating..." : "Creating..."}
            </>
          ) : (
            `${isEditing ? "Update" : "Create"} Base Unit`
          )}
        </button>
      </div>
    </div>
  );
};
